import { Input } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { fmtPercentInput, parsePercentInput } from './calc/formatters';

const PERCENT_DRAFT_PATTERN = /^-?\d*(\.\d*)?$/;

function formatStoredPercent(value, fractionDigits) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '';
  }
  return fmtPercentInput(value, fractionDigits);
}

function shouldCommitDraft(draft) {
  if (draft === '' || draft === '.' || draft === '-') {
    return false;
  }
  return !draft.endsWith('.');
}

function PercentInput({
  value,
  onChange,
  allowEmpty = false,
  fractionDigits = 2,
  ...inputProps
}) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(formatStoredPercent(value, fractionDigits));
    }
  }, [value, focused, fractionDigits]);

  const displayValue = focused ? draft : formatStoredPercent(value, fractionDigits);

  return (
    <Input
      {...inputProps}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={(event) => {
        setFocused(true);
        const initial =
          value != null && Number.isFinite(Number(value)) ? String(value) : '';
        setDraft(initial);
        inputProps.onFocus?.(event);
      }}
      onChange={(event) => {
        const next = event.target.value.replace(/,/g, '');
        if (next !== '' && !PERCENT_DRAFT_PATTERN.test(next)) {
          return;
        }
        setDraft(next);
        if (!shouldCommitDraft(next)) {
          if (allowEmpty && next === '') {
            onChange(null);
          }
          return;
        }
        onChange(parsePercentInput(next, fractionDigits));
      }}
      onBlur={(event) => {
        setFocused(false);
        if (draft === '' || draft === '.' || draft === '-') {
          onChange(allowEmpty ? null : 0);
        } else {
          onChange(parsePercentInput(draft, fractionDigits));
        }
        inputProps.onBlur?.(event);
      }}
    />
  );
}

PercentInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  allowEmpty: PropTypes.bool,
  fractionDigits: PropTypes.number,
};

export default PercentInput;
